import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

// Register Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ConstructionTable2 = ({ data }) => {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  const record = data[0];
  const years = Object.keys(record).filter((k) => !isNaN(k));
  const values = years
    .filter((year) => year <= 2024)
    .map((year) => record[year]);

  // Chart setup
  const chartData = {
    labels: years.filter((year) => year <= 2024),
    datasets: [
      {
        label: record.sex || "Female",
        data: values,
        borderColor: "#EC4899",
        pointBackgroundColor: (ctx) =>
          ctx.dataIndex === values.length - 1 ? "#EC4899" : "#EC4899",
        backgroundColor: "rgba(236, 72, 153, 0.3)",
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.formattedValue} employees`,
        },
      },
      title: {
        display: true,
        text: "📊 Female Employment Trend in Construction (2018-2024)",
        font: { size: 14, weight: "bold" },
        padding: { top: 10, bottom: 10 },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 12 },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
        },
      },
      y: {
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 12 },
        },
        ticks: {
          callback: function (value) {
            return value.toLocaleString("en-US");
          },
          font: { size: 11 },
        },
      },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("construction_employment.pdf");
    });
  };

  // Growth %
  const growthPercent = (
    ((values[values.length - 1] - values[0]) / values[0]) *
    100
  ).toFixed(1);

  return (
    <>
      <div
        ref={pdfRef}
        className="content-to-export p-3 sm:p-4 bg-white rounded-xl shadow-md w-full max-w-full overflow-hidden"
      >
        <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center text-gray-800">
          Female Employment Trend for Construction Industry (2018–2024)
        </h2>

        {/* Summary Cards - Single Row */}
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="p-3 bg-pink-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Growth Since 2018
            </p>
            <p className="text-lg font-bold text-pink-800">+{growthPercent}%</p>
          </div>
          <div className="p-3 bg-yellow-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Strongest Growth Year
            </p>
            <p className="text-lg font-bold text-yellow-800">2022 (+12%)</p>
          </div>
          <div className="p-3 bg-green-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              2024 Employment
            </p>
            <p className="text-lg font-bold text-green-800">
              {values[values.length - 1].toLocaleString("en-US")} 👷‍♀️
            </p>
          </div>
        </div>

        {/* Table + Chart Side-by-Side Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-sm rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-sm text-left">Year</th>
                    <th className="p-2 text-sm" style={{ textAlign: 'right' }}>Employment Count</th>
                  </tr>
                </thead>
                <tbody>
                  {years
                    .filter((year) => year <= 2024)
                    .map((year, idx) => (
                      <tr
                        key={idx}
                        className={`${
                          year === "2024"
                            ? "bg-green-100 font-bold"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="p-2 text-sm text-left">{year}</td>
                        <td className="p-2 text-sm" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {values[idx].toLocaleString("en-US")}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="download-btn bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg text-sm"
        >
          <span>Download as PDF</span>
          <img className="w-[1.2rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default ConstructionTable2;
